import clsx from 'clsx';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { CLASS_LABELS, type HourBucketDto } from '@workforce/shared';
import { formatMinutes, t } from '@/lib/i18n';

/**
 * Small hand-built SVG charts for the productivity pages (no chart library).
 * Conventions: thin columns (≤ 24 px) with a 4 px rounded top, a 2 px surface gap
 * between stacked segments, hairline grid, one y-axis, a legend on every chart with
 * more than one series, and a hover tooltip on every mark. Colours come from the
 * --c-* theme variables so light and dark mode each use their own validated steps.
 */

export const CLASS_KEYS = ['productive', 'neutral', 'unknown', 'unproductive', 'idle'] as const;
export type ClassKey = (typeof CLASS_KEYS)[number];
export const CLASS_FILL: Record<ClassKey, string> = {
  productive: 'rgb(var(--c-productive))',
  neutral: 'rgb(var(--c-neutral))',
  unknown: 'rgb(var(--c-unknown))',
  unproductive: 'rgb(var(--c-unproductive))',
  idle: 'rgb(var(--c-idle))',
};

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e!.contentRect.width)));
    ro.observe(el);
    setW(Math.floor(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** Clean hour ticks (0, 2 h, 4 h …) for a maximum given in minutes. */
function hourTicks(maxMinutes: number): number[] {
  const maxH = Math.max(1, maxMinutes / 60);
  const step = [0.25, 0.5, 1, 2, 4, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000].find((s) => maxH / s <= 4) ?? 1000;
  const out: number[] = [];
  for (let v = 0; v <= maxH + 1e-9 || out.length < 2; v += step) out.push(v * 60);
  if (out[out.length - 1]! < maxMinutes) out.push(out[out.length - 1]! + step * 60);
  return out;
}
const tickLabel = (m: number) => (m === 0 ? '0' : t('{n}h', { n: m % 60 === 0 ? m / 60 : (m / 60).toFixed(m % 30 === 0 ? 1 : 2) }));

/** Rect with only the top corners rounded (data end), square at the baseline. */
function topRounded(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.min(r, h, w / 2);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

export function ChartLegend({ keys = CLASS_KEYS as readonly ClassKey[] }: { keys?: readonly ClassKey[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
      {keys.map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CLASS_FILL[k] }} />
          {t(CLASS_LABELS[k])}
        </span>
      ))}
    </div>
  );
}

function Tooltip({ x, y, width, children }: { x: number; y: number; width: number; children: ReactNode }) {
  const w = 200;
  const left = Math.min(Math.max(0, x - w / 2), Math.max(0, width - w));
  return (
    <div
      dir={document.documentElement.dir || 'ltr'}
      className="pointer-events-none absolute z-10 rounded-lg border border-slate-200 bg-surface px-3 py-2 text-xs shadow-lg"
      style={{ left, top: Math.max(0, y), width: w }}
      role="tooltip"
    >
      {children}
    </div>
  );
}

function TooltipRows({ values, extra }: { values: Partial<Record<ClassKey, number>>; extra?: ReactNode }) {
  const total = CLASS_KEYS.reduce((a, k) => a + (values[k] ?? 0), 0);
  return (
    <>
      {[...CLASS_KEYS].reverse().map((k) =>
        values[k] ? (
          <div key={k} className="flex items-center gap-2 py-0.5">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: CLASS_FILL[k] }} />
            <span className="flex-1 text-slate-600">{t(CLASS_LABELS[k])}</span>
            <span className="tabular-nums text-slate-900">{formatMinutes(values[k]!)}</span>
          </div>
        ) : null,
      )}
      <div className="mt-1 flex justify-between border-t border-slate-100 pt-1 text-slate-500">
        <span>{t('Total')}</span>
        <span className="tabular-nums">{formatMinutes(total)}</span>
      </div>
      {extra}
    </>
  );
}

export interface StackDatum {
  key: string;
  label: string;
  /** Longer label for the tooltip. */
  title?: string;
  values: Partial<Record<ClassKey, number>>;
  extra?: ReactNode;
}

/** Stacked columns of minutes by class (per day, per hour …). */
export function StackedColumns({ data, height = 220, ariaLabel, onSelect }: { data: StackDatum[]; height?: number; ariaLabel: string; onSelect?: (key: string) => void }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const m = { l: 36, r: 4, t: 8, b: 22 };
  const plotW = Math.max(0, width - m.l - m.r);
  const plotH = height - m.t - m.b;
  const totals = data.map((d) => CLASS_KEYS.reduce((a, k) => a + (d.values[k] ?? 0), 0));
  const ticks = hourTicks(Math.max(0, ...totals));
  const maxY = ticks[ticks.length - 1]!;
  const band = data.length ? plotW / data.length : 0;
  const barW = Math.max(2, Math.min(24, band * 0.7));
  const every = Math.max(1, Math.ceil((data.length * 40) / Math.max(plotW, 1)));
  const y = (v: number) => m.t + plotH - (v / maxY) * plotH;
  const GAP = 2;
  return (
    <div ref={ref} dir="ltr" className="relative w-full" onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block">
          {ticks.map((tk) => (
            <g key={tk}>
              <line x1={m.l} x2={width - m.r} y1={y(tk)} y2={y(tk)} stroke="rgb(var(--slate-200))" strokeWidth={1} />
              <text x={m.l - 6} y={y(tk)} dy="0.32em" textAnchor="end" className="fill-slate-400 text-[10px] tabular-nums">
                {tickLabel(tk)}
              </text>
            </g>
          ))}
          {data.map((d, i) => {
            const cx = m.l + band * i + band / 2;
            const x = cx - barW / 2;
            let acc = 0;
            const segs = CLASS_KEYS.filter((k) => (d.values[k] ?? 0) > 0);
            return (
              <g key={d.key} opacity={hover === null || hover === i ? 1 : 0.45}>
                {segs.map((k, j) => {
                  const v = d.values[k]!;
                  const y0 = y(acc);
                  acc += v;
                  const y1 = y(acc);
                  const top = j === segs.length - 1;
                  const h = Math.max(0, y0 - y1 - (j > 0 ? GAP : 0));
                  if (h <= 0) return null;
                  const yy = y1;
                  return top ? (
                    <path key={k} d={topRounded(x, yy, barW, h, 4)} fill={CLASS_FILL[k]} />
                  ) : (
                    <rect key={k} x={x} y={yy} width={barW} height={h} fill={CLASS_FILL[k]} />
                  );
                })}
                {i % every === 0 && (
                  <text x={cx} y={height - 6} textAnchor="middle" className="fill-slate-500 text-[10px]">
                    {d.label}
                  </text>
                )}
                <rect
                  x={m.l + band * i}
                  y={m.t}
                  width={band}
                  height={plotH}
                  fill="transparent"
                  className={onSelect ? 'cursor-pointer' : undefined}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onClick={() => onSelect?.(d.key)}
                  tabIndex={-1}
                />
              </g>
            );
          })}
          <line x1={m.l} x2={width - m.r} y1={y(0)} y2={y(0)} stroke="rgb(var(--slate-300))" strokeWidth={1} />
        </svg>
      )}
      {hover !== null && data[hover] && (
        <Tooltip x={m.l + band * hover + band / 2} y={8} width={width}>
          <p className="mb-1 font-medium text-slate-900">{data[hover]!.title ?? data[hover]!.label}</p>
          <TooltipRows values={data[hover]!.values} extra={data[hover]!.extra} />
        </Tooltip>
      )}
    </div>
  );
}

/** Scores per person as horizontal bars, with the target marked. */
export function ScoreBars({
  rows,
  target,
  onSelect,
}: {
  rows: { key: string; label: string; pct: number | null; detail?: string }[];
  target: number;
  onSelect?: (key: string) => void;
}) {
  const tone = (p: number) => (p >= target ? CLASS_FILL.productive : p >= target - 20 ? CLASS_FILL.unknown : CLASS_FILL.unproductive);
  return (
    <div className="space-y-2">
      <div className="relative ms-[9.5rem] me-12 h-4 text-[10px] text-slate-500">
        <span className="absolute -translate-x-1/2 whitespace-nowrap rtl:translate-x-1/2" style={{ insetInlineStart: `${target}%` }}>{t('Target {pct}%', { pct: target })}</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          className={clsx('group flex items-center gap-3 text-sm', onSelect && 'cursor-pointer')}
          onClick={() => onSelect?.(r.key)}
          title={r.detail}
        >
          <span className={clsx('w-36 shrink-0 truncate', onSelect && 'group-hover:text-brand-700 group-hover:underline')}>{r.label}</span>
          <div className="relative h-3 flex-1 rounded-full bg-slate-100">
            {r.pct != null && r.pct > 0 && <div className="h-3 rounded-full" style={{ width: `${Math.min(100, r.pct)}%`, background: tone(r.pct) }} />}
            <div className="absolute inset-y-[-3px] w-px bg-slate-400" style={{ insetInlineStart: `${target}%` }} aria-hidden />
          </div>
          <span className="w-10 shrink-0 text-end font-semibold tabular-nums text-slate-900">{r.pct == null ? '—' : `${r.pct}%`}</span>
        </div>
      ))}
    </div>
  );
}

/** Width of one hour in HourStrip (px), so headers can line up with it. */
export const HOUR_CELL = 10;

/** A day's 24 hours as tiny stacked columns (for a table row). */
export function HourStrip({ hours, from, to }: { hours: HourBucketDto[]; from?: number; to?: number }) {
  const shown = hours.filter((h, i) => (from == null || i >= from) && (to == null || i <= to));
  const H = 22;
  const W = HOUR_CELL - 2;
  const G = 2;
  const label = shown
    .filter((h) => CLASS_KEYS.some((k) => h[k] > 0))
    .map((h) => `${String(h.hour).padStart(2, '0')}:00 ${CLASS_KEYS.filter((k) => h[k] > 0).map((k) => `${t(CLASS_LABELS[k])} ${formatMinutes(h[k])}`).join(t(', '))}`)
    .join('; ');
  return (
    <svg width={shown.length * (W + G)} height={H} role="img" aria-label={label || t('No activity')} className="block">
      {shown.map((h, i) => {
        let acc = 0;
        return (
          <g key={h.hour}>
            <rect x={i * (W + G)} y={0} width={W} height={H} rx={1.5} fill="rgb(var(--slate-100))" />
            {CLASS_KEYS.map((k) => {
              const v = Math.min(60, h[k]);
              if (!v) return null;
              const hh = (v / 60) * H;
              acc += hh;
              return <rect key={k} x={i * (W + G)} y={H - acc} width={W} height={hh} fill={CLASS_FILL[k]} />;
            })}
            <title>{`${String(h.hour).padStart(2, '0')}:00–${String((h.hour + 1) % 24).padStart(2, '0')}:00  ${CLASS_KEYS.filter((k) => h[k] > 0).map((k) => `${t(CLASS_LABELS[k])} ${formatMinutes(h[k])}`).join(' · ') || t('No activity')}`}</title>
          </g>
        );
      })}
    </svg>
  );
}

/** Which hour indexes to show so every row's strip covers the busy part of the day. */
export function activeHourRange(all: HourBucketDto[][]): { from: number; to: number } {
  let from = 23;
  let to = 0;
  for (const hours of all) {
    hours.forEach((h, i) => {
      if (CLASS_KEYS.some((k) => h[k] > 0)) {
        from = Math.min(from, i);
        to = Math.max(to, i);
      }
    });
  }
  if (from > to) return { from: 0, to: 23 };
  return { from: Math.max(0, from - 1), to: Math.min(23, to + 1) };
}

/** Stat tile: label, value, optional note. */
export function StatTile({ label, value, note, hero }: { label: string; value: ReactNode; note?: ReactNode; hero?: boolean }) {
  return (
    <div className="bg-surface px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={clsx('mt-0.5 font-semibold text-slate-900', hero ? 'text-3xl' : 'text-lg')}>{value}</p>
      {note && <p className="mt-0.5 text-xs text-slate-500">{note}</p>}
    </div>
  );
}
